import { useEffect, useMemo, useState } from "react"
import axios from "axios"

export default function GroupingModal({ open, onClose, group, onSaved }) {
  const apiBaseUrl = process.env.REACT_APP_INVENTORY_API_URL

  const isEdit = !!group?.id

  const [name, setName] = useState("")
  const [selectedIds, setSelectedIds] = useState([]) // ordered
  const [search, setSearch] = useState("")
  const [options, setOptions] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(false)

  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})
  const [imageFile, setImageFile] = useState(null)
    const [imagePreview, setImagePreview] = useState("")
    const [removeImage, setRemoveImage] = useState(false)

    // optional: if your backend returns relative paths and you have base URL
    const assetBaseUrl = process.env.REACT_APP_INVENTORY_API_URL || ""
    const resolveImage = (path) => {
    if (!path) return ""
    if (String(path).startsWith("http")) return path
    const clean = String(path).replace(/^\/+/, "")
    return assetBaseUrl ? `${assetBaseUrl}/${clean}` : `/${clean}`
    }


  // init when open
  useEffect(() => {
    if (!open) return

    setErrors({})
    setSearch("")
    setImageFile(null)
    setRemoveImage(false)
    if (isEdit) {
      setName(group.name || "")
      const items = group.product_types || group.productTypes || []
      // Use pivot.sort_order if present; else current order
      const ordered = [...items]
        .sort((a, b) => (a?.pivot?.sort_order ?? 999) - (b?.pivot?.sort_order ?? 999))
        .map((x) => x.id)

      setSelectedIds(ordered)
        setImagePreview(group?.image_url ? resolveImage(group.image_url) : "")
    } else {
      setName("")
      setSelectedIds([])
    setImagePreview("")
    }
  }, [open, isEdit, group])

  const onPickImage = (e) => {
    const f = e?.target?.files?.[0]
    if (!f) return
    setRemoveImage(false)
    setImageFile(f)
    setImagePreview(URL.createObjectURL(f))
    }

    const clearImage = () => {
    setImageFile(null)
    setImagePreview("")
    setRemoveImage(true) // tells backend to remove existing image
    }

    // optional: use first selected product type image
    const useFirstItemImage = () => {
    const first = selectedItems?.[0]
    if (!first?.image) return
    setImageFile(null)
    setRemoveImage(false)
    setImagePreview(resolveImage(first.image))
    // NOTE: if you want backend to copy from first item, you can also just leave group image empty;
    // this is only for preview clarity in UI.
    }


  // load product types options (once each open)
  useEffect(() => {
    if (!open) return

    let cancelled = false
    const load = async () => {
      setLoadingOptions(true)
      try {
        const res = await axios.get(`${apiBaseUrl}/product/type`, {
          params: { limit: 1000, order_by: "name", direction: "asc" },
        })
        const list = res?.data?.data?.data ?? res?.data ?? []
        console.log(list,"list")
        if (!cancelled) setOptions(Array.isArray(list) ? list : [])
      } catch (e) {
        if (!cancelled) setOptions([])
      } finally {
        if (!cancelled) setLoadingOptions(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [open, apiBaseUrl])

  const filteredOptions = useMemo(() => {
    const q = (search || "").trim().toLowerCase()
    return (options || [])
      .filter((x) => x?.record_status !== "D")
      .filter((x) => (q ? String(x.name || "").toLowerCase().includes(q) : true))
  }, [options, search])

  const selectedItems = useMemo(() => {
    const map = new Map((options || []).map((x) => [x.id, x]))
    return selectedIds.map((id) => map.get(id)).filter(Boolean)
  }, [options, selectedIds])

  const togglePick = (id) => {
    setErrors((e) => ({ ...e, items: undefined }))
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
    //   if (prev.length >= 4) {
    //     setErrors((e) => ({ ...e, items: "Maksimal 4 item." }))
    //     return prev
    //   }
      return [...prev, id]
    })
  }

  const move = (id, dir) => {
    setSelectedIds((prev) => {
      const idx = prev.indexOf(id)
      if (idx < 0) return prev
      const next = [...prev]
      const swap = dir === "up" ? idx - 1 : idx + 1
      if (swap < 0 || swap >= next.length) return prev
      ;[next[idx], next[swap]] = [next[swap], next[idx]]
      return next
    })
  }

  const validate = () => {
    const next = {}
    if (!name.trim()) next.name = "Nama group wajib diisi."
    if (selectedIds.length < 1) next.items = "Minimal pilih 1 item."
    // if (selectedIds.length > 4) next.items = "Maksimal 4 item."
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const submit = async () => {
    if (!validate()) return

    setSaving(true)
    setErrors((e) => ({ ...e, submit: undefined }))

    try {
        const itemsPayload = selectedIds.map((id, idx) => ({
        product_type_id: id,
        sort_order: idx + 1,
        }))

        // Always use FormData (simpler) because image is optional
        const fd = new FormData()
        fd.append("name", name.trim())
        fd.append("items", JSON.stringify(itemsPayload))
        if (imageFile) fd.append("image", imageFile)
        if (removeImage) fd.append("remove_image", "1")

        if (!isEdit) {
        await axios.post(`${apiBaseUrl}/product-type-groups`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        })
        } else {
        await axios.post(`${apiBaseUrl}/product-type-groups/${group.id}?_method=PUT`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        })
        }

        onClose()
        onSaved?.()
    } catch (err) {
        const msg =
        err?.response?.data?.message ||
        "Gagal menyimpan group. Periksa endpoint/payload."
        setErrors((e) => ({ ...e, submit: msg }))
    } finally {
        setSaving(false)
    }
    }


  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={saving ? undefined : onClose} />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <div className="space-y-0.5">
              <div className="text-sm font-semibold">
                {isEdit ? "Edit Group Product Type" : "Buat Group Product Type"}
              </div>
              {/* <div className="text-xs text-neutral-500">
                Pilih maksimal 4 product type dan atur urutannya.
              </div> */}
            </div>

            <button
              type="button"
              onClick={saving ? undefined : onClose}
              className="px-3 py-2 text-xs border rounded-xl hover:bg-neutral-50"
            >
              Tutup
            </button>
          </div>

          <div className="p-5 space-y-5">
            {errors.submit && (
              <div className="p-3 text-xs border rounded-xl border-red-200 bg-red-50 text-red-700">
                {errors.submit}
              </div>
            )}

            <div className="space-y-1">
              <label className="text-xs font-medium">Nama Group</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                type="text"
                placeholder="Contoh: Frame Group"
                className="w-full px-4 py-3 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              />
              {errors.name && <div className="text-xs text-red-600">{errors.name}</div>}
            </div>

            <div className="space-y-2">
                <label className="text-xs font-medium">Gambar Group</label>

                <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-2xl border overflow-hidden bg-neutral-50 flex items-center justify-center">
                    {imagePreview ? (
                        <img
                        src={imagePreview}
                        alt="group"
                        className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="text-[10px] text-neutral-400 px-2 text-center">
                        Tidak ada gambar
                        </div>
                    )}
                    </div>

                    <div className="flex flex-col gap-2">
                    <label className="inline-flex items-center px-4 py-2 text-xs border rounded-xl hover:bg-neutral-50 cursor-pointer">
                        <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={onPickImage}
                        disabled={saving}
                        />
                        {imagePreview ? "Ganti Gambar" : "Upload Gambar"}
                    </label>

                    <div className="flex items-center gap-2">
                        <button
                        type="button"
                        onClick={useFirstItemImage}
                        className="px-3 py-2 text-xs border rounded-xl hover:bg-neutral-50 disabled:opacity-50"
                        disabled={saving || selectedItems.length === 0}
                        title="Gunakan gambar item pertama"
                        >
                        Pakai dari item pertama
                        </button>

                        <button
                        type="button"
                        onClick={clearImage}
                        className="px-3 py-2 text-xs border rounded-xl hover:bg-neutral-50 disabled:opacity-50"
                        disabled={saving || (!imagePreview && !group?.image)}
                        title="Hapus gambar group"
                        >
                        Hapus
                        </button>
                    </div>

                    <div className="text-[11px] text-neutral-500">
                        Disarankan 1:1 (square), PNG/JPG/WebP, max 4MB.
                    </div>
                    </div>
                </div>
                </div>


            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium">Pilih Items</label>
                  <div className="text-xs text-neutral-500">{selectedIds.length}/4</div>
                </div>

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  type="text"
                  placeholder={loadingOptions ? "Loading..." : "Cari product type..."}
                  className="w-full px-4 py-3 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                />

                <div className="border rounded-xl overflow-hidden">
                  <div className="max-h-64 overflow-auto divide-y">
                    {filteredOptions.map((pt) => {
                      const checked = selectedIds.includes(pt.id)
                      return (
                        <button
                          key={pt.id}
                          type="button"
                          onClick={() => togglePick(pt.id)}
                          className="w-full flex items-center justify-between px-4 py-3 text-xs hover:bg-neutral-50 text-left"
                        >
                          <div className="flex items-center space-x-3">
                            <input type="checkbox" checked={checked} readOnly className="w-4 h-4" />
                            <span className="font-medium">{pt.name}</span>
                          </div>
                          <span className="text-neutral-400">{pt.id}</span>
                        </button>
                      )
                    })}

                    {filteredOptions.length === 0 && (
                      <div className="px-4 py-6 text-xs text-neutral-500">Tidak ada data.</div>
                    )}
                  </div>
                </div>

                {errors.items && <div className="text-xs text-red-600">{errors.items}</div>}
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium">Urutan Items (Sort Order)</label>
                <div className="border rounded-xl overflow-hidden">
                  <div className="divide-y">
                    {selectedItems.length === 0 && (
                      <div className="px-4 py-6 text-xs text-neutral-500">
                        Belum ada item dipilih.
                      </div>
                    )}

                    {selectedItems.map((pt, idx) => (
                      <div key={pt.id} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-7 h-7 text-xs flex items-center justify-center rounded-full border">
                            {idx + 1}
                          </div>
                          <div className="text-xs">
                            <div className="font-medium">{pt.name}</div>
                            <div className="text-neutral-400">{pt.id}</div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => move(pt.id, "up")}
                            disabled={idx === 0}
                            className="w-8 h-8 text-xs border rounded-xl hover:bg-neutral-50 disabled:opacity-40"
                            title="Move up"
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            onClick={() => move(pt.id, "down")}
                            disabled={idx === selectedItems.length - 1}
                            className="w-8 h-8 text-xs border rounded-xl hover:bg-neutral-50 disabled:opacity-40"
                            title="Move down"
                          >
                            ▼
                          </button>
                          <button
                            type="button"
                            onClick={() => togglePick(pt.id)}
                            className="w-8 h-8 text-xs border rounded-xl hover:bg-neutral-50"
                            title="Remove"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="text-xs text-neutral-500">
                  Urutan di sini akan disimpan sebagai sort order (1..n).
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end px-5 py-4 border-t space-x-2">
            <button
              type="button"
              onClick={saving ? undefined : onClose}
              className="px-4 py-3 text-xs border rounded-xl hover:bg-neutral-50 disabled:opacity-50"
              disabled={saving}
            >
              Batal
            </button>
            <button
              type="button"
              onClick={submit}
              className="inline-flex items-center px-4 py-3 text-xs text-white transition bg-neutral-800 rounded-xl active:hover:scale-90 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? "Menyimpan..." : "Simpan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
